"use client";

import React, { useState } from "react";
import { CircleCheck, FileText, ShieldCheck, Info, MessageSquare, User, Sparkles, Lock, Layers3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const citationsData = [{
  id: 1,
  sourceName: "Employee_Handbook_2024_v2.pdf",
  status: "Approved",
  excerpt: "Section 4.2: Remote Work Stipend. Employees transitioning to remote work are eligible for a one-time stipend of $500 to purchase necessary home office equipment. This stipend must be requested through the HR portal within the first 30 days of remote status.",
  confidence: "High"
}, {
  id: 2,
  sourceName: "IT_Asset_Management_Policy.docx",
  status: "Approved",
  excerpt: "Section 2.1: Ownership. All hardware and peripherals purchased with company funds, including remote work stipends, remain the exclusive property of the company and must be returned upon termination of employment or transition back to an in-office role.",
  confidence: "High"
}];
const features = [{
  title: "Refusal over guessing",
  description: "If the answer is not supported by approved documents, EloRAG refuses instead of inventing a response.",
  icon: ShieldCheck
}, {
  title: "Exact traceability",
  description: "Each claim can point back to a specific document and supporting excerpt.",
  icon: FileText
}, {
  title: "Governed retrieval only",
  description: "Draft and Review content stay outside the answering engine until approved.",
  icon: Lock
}];
export default function CitationBasedAnswers() {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [activeCitation, setActiveCitation] = useState(citationsData[0]);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const handleCitationClick = (id: number) => {
    const citation = citationsData.find(c => c.id === id);
    if (citation) {
      setActiveCitation(citation);
      setIsSheetOpen(true);
    }
  };
  return <section id="citation-based-answers" className="py-16 bg-background relative overflow-hidden" ref={sectionRef}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 md:px-8 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className={cn("inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 transition-all duration-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")}>
            <Layers3 className="w-4 h-4" />
            <span>Citation-based answers</span>
          </div>

          <h2 className={cn("text-3xl md:text-4xl font-bold text-foreground mb-6 tracking-tight transition-all duration-700 delay-100", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")}>
            Every answer should be backed by evidence
          </h2>

          <p className={cn("text-lg text-muted-foreground transition-all duration-700 delay-200", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")}>
            EloRAG retrieves only from <strong>Approved</strong> content and returns exact source citations
            so teams can verify what the answer is based on.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className={cn("lg:col-span-7 transition-all duration-1000 delay-300", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8")}>
            <Card className="border border-border shadow-xl shadow-shadow-color/5 overflow-hidden bg-card flex flex-col h-full">
              <div className="bg-muted/50 border-b border-border px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center shadow-sm">
                  <Sparkles className="w-4 h-4 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground leading-none">EloRAG Assistant</h3>
                  <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <Lock className="w-3 h-3" /> Governed Mode Active
                  </span>
                </div>
              </div>

              <div className="p-6 space-y-6 bg-background/50 flex-1">
                <div className="flex items-start gap-4 justify-end">
                  <div className="bg-secondary text-secondary-foreground px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-sm shadow-sm border border-border/50">
                    What is our policy on remote work equipment and stipends?
                  </div>
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0 border border-border">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                    <Sparkles className="w-4 h-4 text-primary" />
                  </div>
                  <div className="bg-card text-card-foreground px-5 py-4 rounded-2xl rounded-tl-sm max-w-[90%] text-sm shadow-sm border border-border leading-relaxed">
                    <p className="mb-3">
                      Based on the approved company policies, employees transitioning to remote work are eligible for a one-time stipend of $500 to purchase necessary home office equipment
                      <Button variant="ghost" size="icon" onClick={() => handleCitationClick(1)} className="inline-flex items-center justify-center w-5 h-5 ml-1 text-[10px] font-bold text-primary bg-primary/10 hover:bg-primary/20 hover:text-primary rounded-sm transition-colors align-super h-auto py-0.5 px-1.5" aria-label="View citation 1">
                        1
                      </Button>
                      .
                    </p>
                    <p>
                      Please note that all hardware and peripherals purchased with these company funds remain the exclusive property of the company and must be returned upon termination of employment
                      <Button variant="ghost" size="icon" onClick={() => handleCitationClick(2)} className="inline-flex items-center justify-center w-5 h-5 ml-1 text-[10px] font-bold text-primary bg-primary/10 hover:bg-primary/20 hover:text-primary rounded-sm transition-colors align-super h-auto py-0.5 px-1.5" aria-label="View citation 2">
                        2
                      </Button>
                      .
                    </p>

                    <div className="mt-4 pt-3 border-t border-border/50 flex items-center gap-2 text-xs text-muted-foreground">
                      <CircleCheck className="w-3.5 h-3.5 text-emerald-500" />
                      Generated from 2 approved sources.
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-muted/30 border-t border-border">
                <div className="bg-background border border-border rounded-lg px-4 py-2.5 flex items-center text-muted-foreground text-sm shadow-sm">
                  <MessageSquare className="w-4 h-4 mr-2 opacity-50" />
                  Ask a question based on approved content...
                </div>
              </div>
            </Card>
          </div>

          <div className={cn("lg:col-span-5 space-y-8 transition-all duration-1000 delay-500", isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8")}>
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Verifiable answers matter
              </h3>
              <p className="text-muted-foreground mb-8">
                In business environments, an answer without a source can become a liability.
                EloRAG makes it easier to verify what the system is actually using.
              </p>
            </div>

            <div className="space-y-6">
              {features.map((feature, i) => <div key={i} data-index={i} className="flex gap-4">
                  <div className="shrink-0 mt-1">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center border border-primary/20">
                      <feature.icon className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-foreground mb-1">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <div className="inline-flex items-center gap-2 text-sm font-medium text-foreground bg-secondary px-4 py-2 rounded-md border border-border">
                <Info className="w-4 h-4 text-primary" />
                Click [1] or [2] in the mockup to view the supporting reference.
              </div>
            </div>
          </div>
        </div>
      </div>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md border-l border-border shadow-2xl sm:w-[400px] p-0 flex flex-col">
          <SheetHeader className="p-6 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-primary" />
              <SheetTitle className="text-lg">Reference Details</SheetTitle>
            </div>
            <SheetDescription>
              Traceability data for the selected citation.
            </SheetDescription>
          </SheetHeader>

          <div className="p-6 flex-1 overflow-y-auto space-y-6">
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Content Status
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 text-sm font-medium">
                <CircleCheck className="w-4 h-4" />
                {activeCitation.status}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                This document has passed Draft and Review and is available for approved retrieval.
              </p>
            </div>

            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Source Document
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card shadow-sm">
                <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4 text-primary" />
                </div>
                <div className="truncate">
                  <div className="text-sm font-medium text-foreground truncate" title={activeCitation.sourceName}>
                    {activeCitation.sourceName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Confidence: {activeCitation.confidence}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Exact Excerpt
              </div>
              <div className="relative">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-md" />
                <div className="bg-muted/50 p-4 pl-5 rounded-r-md border border-l-0 border-border text-sm text-foreground leading-relaxed italic">
                  "{activeCitation.excerpt}"
                </div>
              </div>
            </div>

            <div className="bg-secondary/50 border border-border rounded-lg p-4 mt-8">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  <strong>Governance enforced:</strong> this answer is grounded in approved content only.
                  No fallback knowledge or unapproved sources were used.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-border bg-muted/30">
            <Button variant="outline" className="w-full" onClick={() => setIsSheetOpen(false)}>
              Close Reference
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </section>;
}